/**
 * Which of the table's columns may carry a colour, and on what basis.
 *
 * The rule the whole module enforces: **only a RELATIVE metric is coloured.** A relative metric is
 * one that has already been divided by its own scale — a ratio, a rate, a change against a reference
 * — so the same number means the same thing on a €500 test and a €500k evergreen. An ABSOLUTE metric
 * (spend, purchase value, impressions, purchases, results, ad count, outbound clicks, days active)
 * is a quantity, and no quantity is good or bad on its own: €40k of spend is a budget decision, not
 * a verdict, and colouring it would rank the buyer's own choices. Those columns are listed in
 * `ABSOLUTE_METRIC_COLUMN_IDS` and are never given a `tone`.
 *
 * Being relative is necessary but not sufficient. A relative metric is coloured only when it has a
 * basis that is a property of the ROW — never of the rows currently on screen, which would make the
 * colour a statement about scroll position (see `creative-performance-metrics.ts`). Three bases
 * qualify, and everything here is one of them:
 *
 *   1. **A published band** (`CREATIVE_METRIC_BANDS` in `shared`) — the house doctrine's own
 *      thresholds, already rendered by the creative detail panel. Delegated to `evaluateBand`, which
 *      is what makes the table, the panel and the agent's advice state the same numbers. Most of
 *      these bands are `reference` rather than `verdict`: they STATE their range in the cell's
 *      tooltip and leave the number uncoloured, because Meta's funnel counts are directional for
 *      ad-to-ad comparison and not absolute rates.
 *   2. **A self-referential ratio** — a metric already measured against the row's own reference
 *      point (the brand's CPA target, or the same creative's previous window). Parity is 1.00x, so
 *      no external benchmark is needed at all and the verdict cannot be wrong about a brand it knows
 *      nothing about. These carry the full four-tone scale.
 *   3. **An advisory tone** from the metric registry: a warning that never grades, or a soft nod
 *      that never punishes. Frequency flags `warn` only in the fatigue pairing the doctrine names
 *      (above the threshold WITH the row's own CTR falling by at least the house significance gap —
 *      a stable 5 on its own stays uncoloured); a spend share flags `warn` only at the concentration
 *      level where pausing the creative is a budget decision; AOV tints `good` only when the basket
 *      is materially above the account's own average (a response-level fact stamped on via
 *      `IMetricToneContext`), because an outsized basket EXPLAINS a return. None of these ever
 *      renders the opposite pole.
 *
 * A relative metric with NO basis stays uncoloured: cost per add to cart and revenue per visitor
 * are money with no comparable anchor, and conversion rate has no published ad-level band. Their
 * registry baseline is an account median the API does not ship yet — when it does, it arrives as
 * more context fields here, not as a threshold invented per surface.
 */
import {
    FREQUENCY_FATIGUE_THRESHOLD,
    MIN_IMPRESSIONS_FOR_RATE_VERDICT,
    SIGNIFICANCE_MIN_GAP,
    type ICreativeBandKey,
} from 'shared'

import type { ICreativePerformanceRow } from '@/api/creative-intelligence/get-creative-performance-query'
import { TOP_ADS_DEPENDENCY_SHARE } from '@/features/creative-performance/creative-detail/creative-detail-metric-groups'
import type {
    IMetricToneContext,
    IMetricToneResult,
} from '@/features/creative-performance/creative-performance-tone'

/**
 * The quantity columns, named so the invariant is testable rather than merely intended. A column
 * added here can never be coloured; a column added to the table and to neither list is uncoloured by
 * default, which is the safe direction to fail in.
 */
export const ABSOLUTE_METRIC_COLUMN_IDS = [
    'spend',
    'purchaseValue',
    'impressions',
    'purchases',
    'results',
    'adCount',
    'outboundClicks',
    'daysActive',
] as const

/** The table columns whose verdict used to come from a published band — colour now owned by the gradient. */
const BAND_COLUMNS = {
    hookRate: 'hookRate',
    outboundCtr: 'outboundCtr',
    landingPageViewRate: 'landingPageViewRate',
    addToCartRate: 'addToCartRate',
    cpm: 'cpm',
} as const satisfies Record<string, ICreativeBandKey>

export type IBandColumnId = keyof typeof BAND_COLUMNS

/** Change / target columns — never coloured (sign + arrow only). */
const SELF_REFERENTIAL_COLUMN_IDS = [
    'cpaVsTarget',
    'cpaVsOwnPast',
    'cpaChange',
    'cpmChange',
    'outboundCtrChange',
    'spendChange',
    'roasChange',
    'hookRateChange',
] as const

export type ISelfReferentialColumnId = (typeof SELF_REFERENTIAL_COLUMN_IDS)[number]

const SELF_REFERENTIAL_COLUMNS = Object.fromEntries(SELF_REFERENTIAL_COLUMN_IDS.map((id) => [id, true])) as Record<
    ISelfReferentialColumnId,
    true
>

const asValue = (value: number | null | undefined) =>
    value === null || value === undefined || !Number.isFinite(value) ? null : value

/** A row as the tone functions judge it — the server row plus the response-level context stamps. */
type IJudgedRow = ICreativePerformanceRow & IMetricToneContext

/**
 * Frequency's fatigue flag — the registry's one exception to "frequency is never judged". A level
 * alone still is not: the flag fires only on the PAIRING the fatigue doctrine names, repetition
 * above the threshold while the row's own outbound CTR falls, and only with enough delivery for
 * that CTR move to be stable. Warn-only: there is no good frequency to reward.
 */
function frequencyTone(row: IJudgedRow): IMetricToneResult | null {
    const frequency = asValue(row.frequency)
    if (frequency === null) return null
    const ctrChange = asValue(row.outboundCtrChange)
    // The CTR leg must clear the same significance gap the self-referential columns use: a -5% wobble
    // the outboundCtrChange cell itself would call "too close to call" must not amber the frequency
    // cell in the same row.
    if (
        frequency > FREQUENCY_FATIGUE_THRESHOLD &&
        ctrChange !== null &&
        ctrChange <= -SIGNIFICANCE_MIN_GAP &&
        Number(row.impressions ?? 0) >= MIN_IMPRESSIONS_FOR_RATE_VERDICT
    ) {
        return {
            tone: 'warn',
            reason: `${frequency.toFixed(1)} views per person while outbound CTR falls ${Math.round(Math.abs(ctrChange) * 100)}% — the fatigue pairing worth a check.`,
        }
    }
    return neutral(
        `Neither high nor low is good on its own — flagged only above ${FREQUENCY_FATIGUE_THRESHOLD} while outbound CTR falls by at least ${Math.round(SIGNIFICANCE_MIN_GAP * 100)}%.`
    )
}

/**
 * A spend share's concentration flag. Allocation is not performance, so a share is never good or
 * bad — but past the dependency threshold the container leans on this one creative hard enough
 * that pausing it is a budget decision, which is worth a caution before anyone acts on a verdict
 * two columns over.
 */
function spendShareTone(
    share: number | null | undefined,
    scope: 'ad set' | 'campaign' | 'account'
): IMetricToneResult | null {
    const value = asValue(share)
    if (value === null) return null
    if (value >= TOP_ADS_DEPENDENCY_SHARE) {
        return {
            tone: 'warn',
            reason: `${Math.round(value * 100)}% of the ${scope}'s spend — concentrated enough that pausing this creative is a budget decision.`,
        }
    }
    return neutral(
        `Allocation, not performance — flagged only at ${Math.round(TOP_ADS_DEPENDENCY_SHARE * 100)}% or more of the ${scope}'s spend.`
    )
}

/**
 * The advisory columns — warn-only flags (frequency fatigue, spend-share concentration).
 * AOV soft tone removed: AOV is never coloured (brief).
 */
const ADVISORY_COLUMNS = {
    frequency: frequencyTone,
    spendShareOfAdSet: (row: IJudgedRow) => spendShareTone(row.spendShareOfAdSet, 'ad set'),
    spendShareOfCampaign: (row: IJudgedRow) => spendShareTone(row.spendShareOfCampaign, 'campaign'),
    spendShareOfAccount: (row: IJudgedRow) => spendShareTone(row.spendShareOfAccount, 'account'),
} as const satisfies Record<string, (row: IJudgedRow) => IMetricToneResult | null>

export type IAdvisoryColumnId = keyof typeof ADVISORY_COLUMNS

/** Every column this module can colour or annotate. */
export type IRelativeToneColumnId = IBandColumnId | ISelfReferentialColumnId | IAdvisoryColumnId

const neutral = (reason: string): IMetricToneResult => ({ tone: 'neutral', reason })

/**
 * The verdict for one relative column on one row, or `null` when this column carries none.
 *
 * `null` is the answer for every absolute column and for every relative column whose row has no
 * value, no basis or too little volume — the cell then renders in the default ink with its ordinary
 * column tooltip, which is the honest rendering of "this was not judged".
 */
export function relativeMetricTone(columnId: string, row: IJudgedRow): IMetricToneResult | null {
    // Doctrine bands and self-referential move columns no longer colour cells — the account-relative
    // gradient owns relative rates/costs, and deltas stay sign+arrow only.
    if (columnId in BAND_COLUMNS) return null
    if (columnId in SELF_REFERENTIAL_COLUMNS) return null

    const advisory = ADVISORY_COLUMNS[columnId as IAdvisoryColumnId] as
        | ((row: IJudgedRow) => IMetricToneResult | null)
        | undefined
    if (advisory) return advisory(row)

    return null
}

/**
 * The `tone` function for a column definition, or `undefined` when this module judges nothing about
 * that column. `undefined` rather than a function that always answers neutral, so an absolute column
 * keeps its column-kind tooltip instead of gaining an empty explanation for a colour it never had.
 */
export function relativeToneForColumn(columnId: string) {
    if (!(columnId in ADVISORY_COLUMNS)) {
        return undefined
    }
    return (row: IJudgedRow) => relativeMetricTone(columnId, row)
}
